import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import { BrandingResponse } from "subModule/src/services/allcommon/interface";
import { useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import DynamicSelect from "common/Select/Select";
import { useSnackbar } from "notistack";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import { useGetBusinessService } from "subModule/src/services/business/useBusiness";
import ServiceByVehicleTypeHeatmap from "components/CustomerAnalytics/ServiceByVehicleTypeHeatmap";
import CustomerRetentionTrend from "components/CustomerAnalytics/CustomerRetentionTrend";
import CustomerRatingFeedback from "components/CustomerAnalytics/CustomerRatingFeedback";
import CustomerVisitPerformance from "components/CustomerAnalytics/CustomerVisitPerformance";
import CustomerStatsWidgets from "components/CustomerAnalytics/CustomerStatsWidgets";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { selectUserInfo } from "features/user/userSlice";
import DashboardPageTitle from "components/Analytics/DashboardPageTitle";

export default function CustomerAnalyticsDashboard() {
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();
  const branding = useSelector(brandingInfo);
  const loggedInUser = useSelector(selectUserInfo);
  const showBusinessFilter = loggedInUser?.type === -1;
  const [stationsLoaded, setStationsLoaded] = useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [businessSearchTerm, setBusinessSearchTerm] = useState<any>("");
  const [businessPageNo, setBusinessPageNo] = useState<number>(1);
  const [businessSearchLoading, setBusinessSearchLoading] = useState<
    string | null
  >(null);
  const [globalFilterApplied, setGlobalFilterApplied] =
    useState<boolean>(false);
  const durationOptions = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "last_6_months", label: t("last_6_months") },
      { value: "last_year", label: t("last_year") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const { control, setValue, watch } = useForm<{ [key: string]: any }>({
    defaultValues: {
      globalFilters: {
        range: { value: "last_6_months", label: t("last_6_months") },
        allStation: true,
        businessId: null,
      },
    },
  });
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const businessDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const firstRender = useRef(true);
  const { data: stationsData, refetch: stationsRefetch } =
    useGetStationDropdownService({
      pageNumber: pageNo,
      size: 50,
      name: dropdownSearchTerm,
    });
  const { data: businessesData, refetch: businessesRefetch } =
    useGetBusinessService({
      pageNumber: businessPageNo,
      size: 50,
      title: businessSearchTerm,
    });
  const themeColors =
    (branding as BrandingResponse)?.attributes?.themeId === 100
      ? (branding as BrandingResponse)?.attributes?.customTheme
      : (themes as any)[
          (branding as BrandingResponse)?.attributes?.themeId || 3
        ];
  const allStation = 0;
  const stations = (stationsData?.pages[0]?.data?.content || []).map(
    (item: { id: number | null; name: string }) => ({
      value: item.id,
      label: item.name,
    }),
  );
  if ((stationsData?.pages[0]?.data?.content?.length ?? 0) > 1) {
    stations.splice(0, 0, { value: allStation, label: t("allStations") });
  }
  const businesses = (businessesData?.pages[0]?.data?.content || []).map(
    (item) => ({
      value: item.id,
      label: item.title || "",
    }),
  );
  useEffect(() => {
    const getStationsData = setTimeout(async () => {
      await stationsRefetch();
      if (showBusinessFilter) await businessesRefetch();
      setStationsLoaded(true);
    }, 50);
    return () => clearTimeout(getStationsData);
  }, []);
  useEffect(() => {
    if (firstRender.current && stationsLoaded && stations.length > 0) {
      firstRender.current = false;
      const defaultStation =
        stations.find((s) => s.value === allStation) ||
        (stations.length > 1 ? stations[0] : stations[0]) ||
        null;
      setValue("globalFilters.stationId", defaultStation);
      setValue(
        "globalFilters.allStation",
        defaultStation?.value === allStation,
      );
      setValue("globalFilters.range", {
        value: "last_6_months",
        label: t("last_6_months"),
      });
    }
  }, [stationsLoaded, stations]);
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => stationsRefetch().finally(() => setSearchLoading(null)),
      300,
    );
  };
  const handleBusinessDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setBusinessPageNo(1);
      setBusinessSearchTerm(search);
      if (search) setBusinessSearchLoading(typeName);
    } else {
      setBusinessPageNo(businessPageNo + 1);
      setBusinessSearchLoading(typeName);
    }
    if (businessDebounceTimer.current)
      clearTimeout(businessDebounceTimer.current);
    businessDebounceTimer.current = setTimeout(
      () => businessesRefetch().finally(() => setBusinessSearchLoading(null)),
      300,
    );
  };

  return (
    <Grid container spacing={2} alignItems="stretch">
      <Grid
        item
        xs={12}
        className="mb-[-8px] sticky top-[43px] z-10"
        style={{ background: themeColors?.appBg || "#F1F6F9" }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4} lg={4}>
            <DashboardPageTitle
              title={t("customerAnalyticsDashboard")}
              color={themeColors?.primary}
            />
          </Grid>
          <Grid item xs={12} md={8} lg={8}>
            <Grid container spacing={2} className="justify-end pt-2">
              {showBusinessFilter && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Controller
                    name="globalFilters.businessId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={""}
                        {...field}
                        options={businesses}
                        isSearchable
                        isClearable
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                          setGlobalFilterApplied(true);
                        }}
                        isLoading={businessSearchLoading === "globalBusiness"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          ) {
                            handleBusinessDropdownActions(
                              "globalBusiness",
                              "searchIt",
                              search,
                            );
                          } else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (businessSearchTerm || businessPageNo > 1) {
                              handleBusinessDropdownActions(
                                "globalBusiness",
                                "searchIt",
                                "",
                              );
                            } else setBusinessSearchTerm("");
                            setBusinessPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(businessesData?.pages[0]?.data as any)?.last) {
                            handleBusinessDropdownActions(
                              "globalBusiness",
                              "loadNext",
                            );
                          }
                        }}
                        page={businessPageNo}
                        defaultPlaceholder={t("business")}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Controller
                  name="globalFilters.stationId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={stations}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        if (selectedOption?.value === 0) {
                          setValue("globalFilters.allStation", true);
                        } else {
                          setValue("globalFilters.allStation", false);
                        }
                        setGlobalFilterApplied(true);
                      }}
                      isLoading={searchLoading === "globalFilters"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        ) {
                          handleDropdownActions(
                            "globalFilters",
                            "searchIt",
                            search,
                          );
                        } else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || pageNo > 1) {
                            handleDropdownActions(
                              "globalFilters",
                              "searchIt",
                              "",
                            );
                          } else setDropdownSearchTerm("");
                          setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(stationsData?.pages[0]?.data as any)?.last) {
                          handleDropdownActions("globalFilters", "loadNext");
                        }
                      }}
                      page={pageNo}
                      defaultPlaceholder={t("station")}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Controller
                  name="globalFilters.range"
                  control={control}
                  defaultValue={null}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={durationOptions}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setGlobalFilterApplied(true);
                      }}
                      defaultPlaceholder={t("duration")}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <CustomerStatsWidgets
          themes={themeColors}
          enqueueSnackbar={enqueueSnackbar}
          globalFilters={watch("globalFilters")}
          globalFilterApplied={globalFilterApplied}
          setGlobalFilterApplied={setGlobalFilterApplied}
          stationsLoaded={stationsLoaded}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={5}
        lg={5}
        xl={5}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
          maxHeight: { xs: 360, md: 340 },
          minWidth: 0,
          "& > *": { flex: 1, minHeight: 0, width: "100%" },
        }}
      >
        <ServiceByVehicleTypeHeatmap
          themes={themeColors}
          enqueueSnackbar={enqueueSnackbar}
          globalFilters={watch("globalFilters")}
          globalFilterApplied={globalFilterApplied}
          setGlobalFilterApplied={setGlobalFilterApplied}
          stationsLoaded={stationsLoaded}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={7}
        lg={7}
        xl={7}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
          maxHeight: { xs: 360, md: 340 },
          minWidth: 0,
          "& > *": { flex: 1, minHeight: 0, width: "100%" },
        }}
      >
        <CustomerRetentionTrend
          themes={themeColors}
          enqueueSnackbar={enqueueSnackbar}
          globalFilters={watch("globalFilters")}
          globalFilterApplied={globalFilterApplied}
          setGlobalFilterApplied={setGlobalFilterApplied}
          stationsLoaded={stationsLoaded}
        />
      </Grid>
      <Grid item xs={12} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            gap: 2,
            maxHeight: { xs: "none", md: 340 },
            minHeight: { md: 340 },
          }}
        >
          <Box
            sx={{
              flex: { md: "1 1 34%", lg: "1 1 33.333%" },
              width: { xs: "100%", md: "auto" },
              minWidth: 0,
              minHeight: { xs: 320, md: 0 },
              maxHeight: { xs: 360, md: "none" },
              position: "relative",
              alignSelf: "stretch",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: { xs: "100%", md: "100%" },
                minHeight: { xs: 320, md: "100%" },
                position: { xs: "relative", md: "absolute" },
                top: { md: 0 },
                left: { md: 0 },
                right: { md: 0 },
                bottom: { md: 0 },
              }}
            >
              <CustomerRatingFeedback
                themes={themeColors}
                enqueueSnackbar={enqueueSnackbar}
                globalFilters={watch("globalFilters")}
                globalFilterApplied={globalFilterApplied}
                setGlobalFilterApplied={setGlobalFilterApplied}
                stationsLoaded={stationsLoaded}
              />
            </Box>
          </Box>
          <Box
            sx={{
              flex: { md: "1 1 66%", lg: "1 1 66.666%" },
              width: { xs: "100%", md: "auto" },
              minWidth: 0,
              maxHeight: { xs: 360, md: "none" },
              display: "flex",
              flexDirection: "column",
              "& > *": { flex: 1, minHeight: 0, width: "100%" },
            }}
          >
            <CustomerVisitPerformance
              themes={themeColors}
              enqueueSnackbar={enqueueSnackbar}
              globalFilters={watch("globalFilters")}
              globalFilterApplied={globalFilterApplied}
              setGlobalFilterApplied={setGlobalFilterApplied}
              stationsLoaded={stationsLoaded}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
